import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pin, PinOff, Trash2, Save, Lock, Users, StickyNote, Edit2, X } from 'lucide-react';
import { API, fmtDate, inputCls } from './groupMeta';

export const NOTE_CATS = {
  general: { label: 'General', hex: '#94A3B8' }, meeting: { label: 'Meeting', hex: '#38BDF8' },
  concern: { label: 'Concern', hex: '#F43F5E' }, praise: { label: 'Praise', hex: '#10B981' }, follow_up: { label: 'Follow-up', hex: '#F59E0B' },
};

const Briefing = ({ group, canWrite, getAuthHeaders, onChanged }) => {
  const [text, setText] = useState(group.briefing?.text || '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setText(group.briefing?.text || ''); }, [group.briefing?.text]);
  const save = async () => {
    try { await axios.put(`${API}/groups/${group.id}/briefing`, { text }, { headers: getAuthHeaders() }); toast.success('Shared notes saved'); setEditing(false); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="rounded-2xl border border-amber-400/15 bg-amber-500/[0.03] p-4" data-testid="group-briefing">
      <div className="flex items-center justify-between mb-2">
        <p className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-amber-300/80 font-semibold"><StickyNote className="w-3.5 h-3.5" /> Shared notes</p>
        {canWrite && !editing && <button onClick={() => setEditing(true)} data-testid="briefing-edit-btn" className="text-[11px] text-amber-200/80 hover:text-amber-100 inline-flex items-center gap-1"><Edit2 className="w-3 h-3" /> Edit</button>}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} className={`${inputCls} resize-y`} data-testid="briefing-textarea"
            placeholder="Standing notes everyone with access should know: key contacts, agreements, quirks, history…" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setEditing(false); setText(group.briefing?.text || ''); }} className="px-3 py-1.5 text-xs text-white/50"><X className="w-3.5 h-3.5" /></button>
            <button onClick={save} data-testid="briefing-save-btn" className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-semibold text-[#0a0a14] bg-amber-300"><Save className="w-3.5 h-3.5" /> Save</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-white/75 whitespace-pre-line" data-testid="briefing-text">{group.briefing?.text || <span className="text-white/35">No shared notes yet.</span>}</p>
          {group.briefing?.updated_at && <p className="text-[10px] text-white/35 mt-2">Last edited by {group.briefing.updated_by} · {fmtDate(group.briefing.updated_at)}</p>}
        </>
      )}
    </div>
  );
};

export const GroupNotesTab = ({ group, access, currentUsername, getAuthHeaders, onChanged }) => {
  const [f, setF] = useState({ body: '', category: 'general', visibility: 'managers', pinned: false });
  const [cat, setCat] = useState('all');
  const canWrite = group.can_inspect;
  const full = access === 'full';
  const h = () => ({ headers: getAuthHeaders() });
  const notes = (group.notes || []).filter(n => cat === 'all' || n.category === cat);

  const add = async () => {
    if (!f.body.trim()) return toast.error('Write a note first');
    try { await axios.post(`${API}/groups/${group.id}/notes`, f, h()); setF({ ...f, body: '', pinned: false }); toast.success('Note added'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
  };
  const patch = async (n, body) => { try { await axios.patch(`${API}/notes/${n.id}`, body, h()); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const del = async (n) => { if (!window.confirm('Delete this note?')) return; try { await axios.delete(`${API}/notes/${n.id}`, h()); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  return (
    <div className="space-y-4" data-testid="group-notes-tab">
      <Briefing group={group} canWrite={canWrite} getAuthHeaders={getAuthHeaders} onChanged={onChanged} />
      {canWrite && (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 space-y-3">
          <textarea rows={3} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Add a note to the timeline…" className={`${inputCls} resize-none`} data-testid="note-body-input" />
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(NOTE_CATS).map(([k, m]) => (
              <button key={k} onClick={() => setF({ ...f, category: k })} data-testid={`note-cat-${k}`}
                className="px-2.5 py-1 rounded-full text-[10px] border transition-colors"
                style={f.category === k ? { background: `${m.hex}22`, borderColor: m.hex, color: m.hex } : { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>{m.label}</button>
            ))}
            <div className="flex-1" />
            {full && (
              <button onClick={() => setF({ ...f, visibility: f.visibility === 'internal' ? 'managers' : 'internal' })} data-testid="note-visibility-toggle"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] border border-white/[0.1] text-white/60">
                {f.visibility === 'internal' ? <><Lock className="w-3 h-3" /> Executives only</> : <><Users className="w-3 h-3" /> Managers can see</>}
              </button>
            )}
            <button onClick={() => setF({ ...f, pinned: !f.pinned })} data-testid="note-pin-toggle" className={`p-1.5 rounded-lg ${f.pinned ? 'text-amber-300' : 'text-white/35'}`}><Pin className="w-3.5 h-3.5" /></button>
            <button onClick={add} data-testid="note-add-btn" className="px-4 py-1.5 rounded-full text-xs font-semibold text-[#0a0a14] bg-gradient-to-r from-cyan-400 to-violet-400">Add note</button>
          </div>
        </div>
      )}
      <div className="flex gap-1 flex-wrap">
        {[['all', 'All']].concat(Object.entries(NOTE_CATS).map(([k, m]) => [k, m.label])).map(([k, l]) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`notes-filter-${k}`} className={`px-3 py-1 rounded-full text-[11px] border ${cat === k ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-100' : 'border-white/[0.07] text-white/50'}`}>{l}</button>
        ))}
      </div>
      {notes.length === 0 ? <p className="text-sm text-white/40 py-8 text-center">No notes yet.</p> : (
        <div className="space-y-2">
          {notes.map(n => {
            const m = NOTE_CATS[n.category] || NOTE_CATS.general;
            const mine = n.author === currentUsername || full;
            return (
              <div key={n.id} data-testid={`note-${n.id}`} className={`group rounded-xl border p-4 ${n.pinned ? 'border-amber-400/30 bg-amber-500/[0.04]' : 'border-white/[0.06] bg-white/[0.02]'}`} style={{ borderLeft: `3px solid ${m.hex}` }}>
                <div className="flex items-center gap-2 mb-1.5 text-[10px]">
                  <span className="font-semibold uppercase tracking-[0.12em]" style={{ color: m.hex }}>{m.label}</span>
                  {n.pinned && <span className="text-amber-300 inline-flex items-center gap-0.5"><Pin className="w-3 h-3" /> Pinned</span>}
                  {n.visibility === 'internal' && <span className="text-violet-300 inline-flex items-center gap-0.5"><Lock className="w-3 h-3" /> Exec only</span>}
                  <span className="text-white/35">{n.author_name} · {fmtDate(n.created_at)}</span>
                  <div className="flex-1" />
                  {mine && <>
                    <button onClick={() => patch(n, { pinned: !n.pinned })} data-testid={`note-pin-${n.id}`} className="opacity-0 group-hover:opacity-100 p-1 text-white/40 hover:text-amber-300">{n.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</button>
                    <button onClick={() => del(n)} data-testid={`note-delete-${n.id}`} className="opacity-0 group-hover:opacity-100 p-1 text-white/40 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>}
                </div>
                <p className="text-sm text-white/80 whitespace-pre-line">{n.body}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
